import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Stack,
    Chip,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    PictureAsPdf as PictureAsPdfIcon,
    ArrowBack as ArrowBackIcon,
    FileDownload as FileDownloadIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import inventarioInformes, {
    formatMovimientoStockCantidad,
    formatMovimientoStockNumero,
    type MovimientoStockRow,
} from '../../services/inventarioInformes';
import productoService, {
    OPTIONS_PRODUCTO_OPERATIVO,
    type Producto,
} from '../../services/productoService';
import { TIPOS_MOVIMIENTO_STOCK } from '../../constants/tiposMovimientoStock';
import PDFViewer from '../../components/shared/PDFViewer';
import SearchableSelect from '../../components/SearchableSelect';

const umFromProducto = (producto: Producto | Record<string, unknown> | null | undefined): string => {
    if (!producto) return '';
    const p = producto as unknown as Record<string, unknown>;
    const umNav = p.unidadMedida;
    const nested =
        umNav && typeof umNav === 'object'
            ? String(
                  (umNav as Record<string, unknown>).idUnidadMedida ??
                      (umNav as Record<string, unknown>).descripcion ??
                      '',
              ).trim()
            : typeof umNav === 'string'
              ? umNav.trim()
              : '';
    return String(p.idUnidadMedida ?? p.um ?? p.Um ?? nested ?? '').trim();
};

const enrichMovimientosConUm = async (rows: MovimientoStockRow[]): Promise<MovimientoStockRow[]> => {
    const codesWithoutUm = Array.from(
        new Set(
            rows
                .filter((r) => r.codArticulo?.trim() && !String(r.unidadMedida ?? '').trim())
                .map((r) => r.codArticulo.trim()),
        ),
    );
    if (!codesWithoutUm.length) return rows;

    const results = await Promise.allSettled(
        codesWithoutUm.map((code) => productoService.getProducto(code, OPTIONS_PRODUCTO_OPERATIVO)),
    );
    const umByCode = new Map<string, string>();
    codesWithoutUm.forEach((code, idx) => {
        const res = results[idx];
        if (res.status === 'fulfilled') {
            const um = umFromProducto(res.value);
            if (um) umByCode.set(code, um);
        }
    });
    if (!umByCode.size) return rows;

    return rows.map((row) => {
        const um = umByCode.get(row.codArticulo.trim());
        return um && !String(row.unidadMedida ?? '').trim() ? { ...row, unidadMedida: um } : row;
    });
};

const TIPO_MOVIMIENTO_STOCK_OPTIONS = Object.entries(TIPOS_MOVIMIENTO_STOCK).map(([value, name]) => {
    const prefix = value.includes('-') ? value.split('-')[0] : value;
    return { value, label: `${prefix} - ${name}` };
});

interface MovimientosFilters {
    fechaDesde: string;
    fechaHasta: string;
    codigoArticulo: string;
    tipoMovimiento: string;
    deposito: string;
}

/** Filtros iniciales; si vienen codigoArticulo/deposito por URL (p. ej. desde Stock por depósito), amplía fechas a un año para ver historial. */
function buildInitialMovimientosFilters(searchParams: URLSearchParams): MovimientosFilters {
    const codigoArticulo = searchParams.get('codigoArticulo') ?? '';
    const deposito = searchParams.get('deposito') ?? '';
    const today = new Date();
    const fechaHasta = today.toISOString().split('T')[0];
    let fechaDesde = fechaHasta;
    if (codigoArticulo.trim() !== '' || deposito.trim() !== '') {
        const d = new Date(today);
        d.setFullYear(d.getFullYear() - 1);
        fechaDesde = d.toISOString().split('T')[0];
    }
    return {
        fechaDesde,
        fechaHasta,
        codigoArticulo,
        tipoMovimiento: '',
        deposito,
    };
}

const defaultClearFilters = (): MovimientosFilters => {
    const today = new Date().toISOString().split('T')[0];
    return {
        fechaDesde: today,
        fechaHasta: today,
        codigoArticulo: '',
        tipoMovimiento: '',
        deposito: '',
    };
};

interface Ubicacion {
    nroUbicacion: number;
    nombreUbicacion: string;
}

type TipoMovimiento = keyof typeof TIPOS_MOVIMIENTO_STOCK;

const getNombreTipoMovimiento = (mov: Pick<MovimientoStockRow, 'tipoMov' | 'descripcionMov'>): string => {
    if (mov.descripcionMov) return mov.descripcionMov;
    const key = Object.keys(TIPOS_MOVIMIENTO_STOCK).find(
        (k) => k === mov.tipoMov || mov.tipoMov?.startsWith(k),
    ) as TipoMovimiento | undefined;
    return key ? TIPOS_MOVIMIENTO_STOCK[key] : mov.tipoMov || '-';
};

const depositoFiltro = (deposito: string) => (deposito ? parseInt(deposito, 10) : undefined);

const formatComprobante = (movimiento: MovimientoStockRow) =>
    movimiento.letra && movimiento.nroSuc != null && movimiento.nroComprobante != null
        ? `${movimiento.letra}-${movimiento.nroSuc.toString().padStart(4, '0')}-${movimiento.nroComprobante.toString().padStart(8, '0')}`
        : '-';

const formatFecha = (fechaMov: string) => new Date(fechaMov).toLocaleDateString('es-AR');

const MovimientosStock: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [movimientos, setMovimientos] = useState<MovimientoStockRow[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalCantidad, setTotalCantidad] = useState(0);
    const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
    const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
    const previewPdfUrlRef = useRef<string | null>(null);
    previewPdfUrlRef.current = previewPdfUrl;

    const initialFilters = useMemo(
        () => buildInitialMovimientosFilters(searchParams),
        [searchParams],
    );

    const [filters, setFilters] = useState<MovimientosFilters>(initialFilters);
    const [applied, setApplied] = useState<MovimientosFilters>(initialFilters);

    useEffect(() => {
        setFilters(initialFilters);
        setApplied(initialFilters);
        setPage(0);
    }, [initialFilters]);

    useEffect(() => {
        return () => {
            if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
        };
    }, []);

    const handleClosePdfPreview = () => {
        setPreviewPdfUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
    };

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await inventarioInformes.getMovimientosStock({
                fechaDesde: applied.fechaDesde || undefined,
                fechaHasta: applied.fechaHasta || undefined,
                codigoArticulo: applied.codigoArticulo || undefined,
                tipoMovimiento: applied.tipoMovimiento || undefined,
                deposito: depositoFiltro(applied.deposito),
                page: page + 1,
                pageSize: rowsPerPage,
            });

            const rows = await enrichMovimientosConUm(response.data ?? []);
            setMovimientos(rows);
            setTotalCount(typeof response.totalCount === 'number' && response.totalCount >= 0 ? response.totalCount : 0);
            setTotalCantidad(response.totalCantidad ?? 0);
        } catch (error: unknown) {
            console.error('Error al cargar movimientos:', error);
            const err = error as { response?: { data?: { error?: string } } };
            const message = err.response?.data?.error || 'Error al cargar los movimientos de stock';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [applied, page, rowsPerPage, enqueueSnackbar]);

    useEffect(() => {
        void loadData();
    }, [loadData]);

    useEffect(() => {
        const loadUbicaciones = async () => {
            try {
                const data = await inventarioInformes.getUbicaciones();
                setUbicaciones(data);
            } catch (error) {
                console.error('Error al cargar ubicaciones:', error);
                enqueueSnackbar('Error al cargar las ubicaciones', { variant: 'error' });
            }
        };
        void loadUbicaciones();
    }, [enqueueSnackbar]);

    const ubicacionOptions = useMemo(
        () =>
            ubicaciones.map((ubicacion) => ({
                value: String(ubicacion.nroUbicacion),
                label: `${ubicacion.nroUbicacion} - ${ubicacion.nombreUbicacion}`,
            })),
        [ubicaciones],
    );

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSearch = () => {
        setPage(0);
        setApplied({ ...filters });
    };

    const handleClearFilters = () => {
        const cleared = defaultClearFilters();
        setFilters(cleared);
        setApplied(cleared);
        setPage(0);
    };

    const handleFilterKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearch();
        }
    };

    const appliedExportParams = {
        fechaDesde: applied.fechaDesde || undefined,
        fechaHasta: applied.fechaHasta || undefined,
        codigoArticulo: applied.codigoArticulo || undefined,
        tipoMovimiento: applied.tipoMovimiento || undefined,
        deposito: depositoFiltro(applied.deposito),
    };

    const handleExportPdf = async () => {
        try {
            setLoading(true);
            const blob = await inventarioInformes.getMovimientosStockPdf(appliedExportParams);

            if (blob.size === 0) {
                enqueueSnackbar('No hay datos para generar el PDF', { variant: 'warning' });
                return;
            }

            const pdfBlob =
                blob instanceof Blob && blob.type === 'application/pdf'
                    ? blob
                    : new Blob([blob], { type: 'application/pdf' });
            const url = URL.createObjectURL(pdfBlob);
            setPreviewPdfUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return url;
            });
        } catch (error: unknown) {
            console.error('Error al generar PDF:', error);
            const err = error as { response?: { data?: { error?: string } } };
            const errorMessage = err.response?.data?.error || 'Error al generar el PDF';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = async () => {
        try {
            setLoading(true);
            const response = await inventarioInformes.getMovimientosStock(appliedExportParams);
            const data = response.data ?? [];
            if (data.length === 0) {
                enqueueSnackbar('No hay datos para exportar', { variant: 'info' });
                return;
            }
            const enriched = await enrichMovimientosConUm(data);
            const sheetRows: Array<Record<string, string | number>> = enriched.map((row, index) => ({
                'N°': index + 1,
                Fecha: formatFecha(row.fechaMov),
                Tipo: getNombreTipoMovimiento(row),
                Comprobante: formatComprobante(row),
                Código: row.codArticulo,
                Descripción: row.descripcion,
                'U.M.': row.unidadMedida ?? '',
                Depósito: row.deposito,
                Cantidad: row.cantidad,
            }));
            sheetRows.push({
                'N°': '',
                Fecha: '',
                Tipo: '',
                Comprobante: '',
                Código: '',
                Descripción: 'Totales',
                'U.M.': '',
                Depósito: '',
                Cantidad: response.totalCantidad,
            });
            const ws = XLSX.utils.json_to_sheet(sheetRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Movimientos stock');
            XLSX.writeFile(wb, `movimientos-stock-${applied.fechaDesde}_${applied.fechaHasta}.xlsx`);
            enqueueSnackbar('Excel exportado correctamente', { variant: 'success' });
        } catch (error: unknown) {
            console.error('Error al exportar Excel:', error);
            const err = error as { response?: { data?: { error?: string } } };
            const errorMessage = err.response?.data?.error || 'Error al exportar a Excel';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const renderCardCampo = (label: string, valor: React.ReactNode) => (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                {label}
            </Typography>
            <Typography variant="body2">{valor}</Typography>
        </Box>
    );

    const renderTotalsBlock = () => (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
            {renderCardCampo('Total cantidad', formatMovimientoStockNumero(totalCantidad))}
        </Paper>
    );

    const renderCardsMobile = () => {
        if (movimientos.length === 0) {
            return (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No hay movimientos para mostrar
                </Typography>
            );
        }

        return (
            <Stack spacing={2}>
                {movimientos.map((movimiento) => (
                    <Paper key={movimiento.nroInterno} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                                {renderCardCampo('Fecha', formatFecha(movimiento.fechaMov))}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo('Tipo', getNombreTipoMovimiento(movimiento))}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo('Comprobante', formatComprobante(movimiento))}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo('Código', movimiento.codArticulo)}
                            </Grid>
                            <Grid item xs={12}>
                                {renderCardCampo('Descripción', movimiento.descripcion)}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo('Depósito', movimiento.deposito)}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo(
                                    'Cantidad',
                                    formatMovimientoStockCantidad(movimiento.cantidad, movimiento.unidadMedida),
                                )}
                            </Grid>
                        </Grid>
                    </Paper>
                ))}
                {movimientos.length > 0 && renderTotalsBlock()}
            </Stack>
        );
    };

    const pagination = (
        <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
                mt: isMobile ? 2 : 0,
                '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1,
                },
            }}
        />
    );

    const exportButtons = (
        <>
            <Button
                variant="outlined"
                color="primary"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handleExportPdf()}
                disabled={loading}
                fullWidth={isMobile}
                sx={{
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                }}
            >
                Generar PDF
            </Button>
            <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={() => void handleExportExcel()}
                disabled={loading}
                fullWidth={isMobile}
                sx={{
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                }}
            >
                Exportar Excel
            </Button>
        </>
    );

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            <Card
                sx={{
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={2}
                        sx={{ mb: 1 }}
                    >
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/inventario/informes')}
                            sx={{ textTransform: 'none' }}
                        >
                            Informes
                        </Button>
                        <Box>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{ fontWeight: 600, color: '#1976d2' }}
                            >
                                Movimientos de Stock
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Consulte el historial de movimientos de stock por artículo, tipo y depósito.
                            </Typography>
                        </Box>
                    </Stack>

                    <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField
                                        fullWidth
                                        label="Fecha Desde"
                                        type="date"
                                        name="fechaDesde"
                                        value={filters.fechaDesde}
                                        onChange={handleFilterChange}
                                        onKeyDown={handleFilterKeyDown}
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField
                                        fullWidth
                                        label="Fecha Hasta"
                                        type="date"
                                        name="fechaHasta"
                                        value={filters.fechaHasta}
                                        onChange={handleFilterChange}
                                        onKeyDown={handleFilterKeyDown}
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField
                                        fullWidth
                                        label="Código Artículo"
                                        name="codigoArticulo"
                                        value={filters.codigoArticulo}
                                        onChange={handleFilterChange}
                                        onKeyDown={handleFilterKeyDown}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <SearchableSelect
                                        label="Tipo Movimiento"
                                        size="small"
                                        allowEmpty
                                        emptyOptionLabel="Todos"
                                        options={TIPO_MOVIMIENTO_STOCK_OPTIONS}
                                        value={filters.tipoMovimiento || null}
                                        onChange={(value) =>
                                            setFilters((prev) => ({ ...prev, tipoMovimiento: value ?? '' }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <SearchableSelect
                                        label="Depósito"
                                        size="small"
                                        allowEmpty
                                        emptyOptionLabel="Todos"
                                        options={ubicacionOptions}
                                        value={filters.deposito || null}
                                        onChange={(value) =>
                                            setFilters((prev) => ({ ...prev, deposito: value ?? '' }))
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                mt={2}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                justifyContent="space-between"
                            >
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSearch}
                                        disabled={loading}
                                        fullWidth={isMobile}
                                    >
                                        Buscar
                                    </Button>
                                    <Button
                                        variant="text"
                                        startIcon={<ClearIcon />}
                                        onClick={handleClearFilters}
                                        disabled={loading}
                                        fullWidth={isMobile}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Limpiar filtros
                                    </Button>
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    {exportButtons}
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>

                    {totalCount > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Chip
                                label={`${totalCount.toLocaleString('es-AR')} registro${totalCount === 1 ? '' : 's'}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        </Box>
                    )}

                    <Box sx={{ position: 'relative' }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255,255,255,0.6)',
                                    zIndex: 1,
                                    borderRadius: 2,
                                    minHeight: 120,
                                }}
                            >
                                <CircularProgress size={36} />
                            </Box>
                        )}

                        {isMobile ? (
                            <Box>
                                {renderCardsMobile()}
                                {pagination}
                            </Box>
                        ) : (
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>U.M.</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Depósito</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Cantidad</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {movimientos.length > 0 ? (
                                            movimientos.map((movimiento) => (
                                                <TableRow key={movimiento.nroInterno} hover>
                                                    <TableCell>{formatFecha(movimiento.fechaMov)}</TableCell>
                                                    <TableCell>{getNombreTipoMovimiento(movimiento)}</TableCell>
                                                    <TableCell>{formatComprobante(movimiento)}</TableCell>
                                                    <TableCell>{movimiento.codArticulo}</TableCell>
                                                    <TableCell>{movimiento.descripcion}</TableCell>
                                                    <TableCell>{movimiento.unidadMedida || '-'}</TableCell>
                                                    <TableCell>{movimiento.deposito}</TableCell>
                                                    <TableCell align="right">
                                                        {formatMovimientoStockCantidad(
                                                            movimiento.cantidad,
                                                            movimiento.unidadMedida,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center">
                                                    No hay movimientos para mostrar
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                    {movimientos.length > 0 && (
                                        <TableFooter>
                                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                                <TableCell colSpan={7} sx={{ fontWeight: 600 }}>
                                                    Totales
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {formatMovimientoStockNumero(totalCantidad)}
                                                </TableCell>
                                            </TableRow>
                                        </TableFooter>
                                    )}
                                </Table>
                                {pagination}
                            </TableContainer>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {previewPdfUrl ? (
                <PDFViewer
                    pdfUrl={previewPdfUrl}
                    onClose={handleClosePdfPreview}
                    title="Movimientos de stock"
                    documentId="movimientos-stock"
                    documentType="informeCliente"
                    downloadFileName="MovimientosStock.pdf"
                />
            ) : null}
        </Box>
    );
};

export default MovimientosStock;
